import { Fragment, createElement, useMemo, useState } from "react";
import type { ComponentType, MouseEvent } from "react";
import styles from "./Sidebar.module.scss";
import { SIDEBAR_SECTION_ORDER, sideBarNavigation } from "@/lib/sidebarNavigation";
import type { SidebarItem, SidebarChild } from "@/lib/sidebarNavigation";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { checkPermission, combineClasses, captalizeEachWord, getCurrentUser } from "@/lib/utils";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import MonogramSVG from "@/assets/svg/LoginPage/MonogramSVG";
import type { RootState } from "@/store/rootReducer";

interface SidebarProps {
  toggleSidebarOpen: (open: boolean) => (e: MouseEvent) => void;
}

const Sidebar = ({ toggleSidebarOpen }: SidebarProps) => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const [openDropdown, setOpenDropdown] = useState<string>("");

  const location = useLocation();

  const email = getCurrentUser("email") as string | undefined;
  const displayName = email
    ? captalizeEachWord((email.split("@")[0] ?? email).replace(/[._]+/g, " "))
    : "Admin User";

  const toggleDropdown = (id: string) => () => {
    setOpenDropdown((prev) => (prev === id ? "" : id));
  };

  const groupedNavigation = useMemo(() => {
    const visibleItems = sideBarNavigation.filter((data: SidebarItem) =>
      checkPermission(data.permissionName),
    );

    return SIDEBAR_SECTION_ORDER.map((section) => ({
      section,
      items: visibleItems.filter((item) => item.section === section),
    })).filter((group) => group.items.length > 0);
  }, []);

  const renderNavItem = (data: SidebarItem) => {
    const children = data.children?.filter((child: SidebarChild) =>
      checkPermission(child.permissionName),
    );
    const isChildRouteActive = Boolean(
      children?.some((child: SidebarChild) => {
        const childTo = child.path ? `${data.path}/${child.path}` : data.path;

        return location.pathname === childTo;
      }),
    );

    if (children?.length) {
      return (
        <Fragment key={data.id}>
          <div
            onClick={toggleDropdown(data.id)}
            className={combineClasses(
              styles.navLink,
              !sidebarOpen && styles.sidebarClosed,
              (openDropdown === data.id || isChildRouteActive) && styles.activeLink,
            )}
          >
            <div className={styles.linksNames}>
              {data.icon && (
                <div className={styles.linkIcon}>
                  {createElement(data.icon as ComponentType<any>)}
                </div>
              )}
              {sidebarOpen && <p className={styles.linkPageName}>{data.pageName}</p>}
            </div>
            {sidebarOpen && (
              <ArrowIcon
                className={styles.arrowIcon}
                style={{
                  transform: openDropdown === data.id ? "rotate(-90deg)" : "rotate(90deg)",
                  transition: "transform ease 300ms",
                }}
              />
            )}
          </div>

          {sidebarOpen && openDropdown === data.id && (
            <div className={styles.ChildrenLinkWrapper}>
              {children.map((child: SidebarChild) => {
                const childTo = child.path ? `${data.path}/${child.path}` : data.path;

                return (
                  <NavLink
                    key={child.id}
                    className={({ isActive }) =>
                      combineClasses(styles.navLink, styles.childNavLink, isActive && styles.activeLink)
                    }
                    to={childTo}
                    end={!child.path}
                    state={{ previousPath: location.pathname }}
                  >
                    <div className={styles.linksNames}>
                      {child.icon && (
                        <div className={styles.linkIcon}>
                          {createElement(child.icon as ComponentType<any>)}
                        </div>
                      )}
                      <p className={styles.linkPageName}>{child.pageName}</p>
                    </div>
                  </NavLink>
                );
              })}
            </div>
          )}
        </Fragment>
      );
    }

    return (
      <NavLink
        key={data.id}
        className={({ isActive }) =>
          combineClasses(
            styles.navLink,
            !sidebarOpen && styles.sidebarClosed,
            isActive && styles.activeLink,
            data.mobileOnly && styles.mobileOnlyLink,
          )
        }
        to={data.path}
        state={{ previousPath: location.pathname }}
        onClick={(e) => {
          toggleDropdown("")();
          if (window.innerWidth <= 500) {
            toggleSidebarOpen(false)(e);
          }
        }}
      >
        <div className={styles.linksNames}>
          {data.icon && (
            <div className={styles.linkIcon}>
              {createElement(data.icon as ComponentType<any>)}
            </div>
          )}
          {sidebarOpen && <p className={styles.linkPageName}>{data.pageName}</p>}
        </div>
      </NavLink>
    );
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className={styles.mobileBackdrop}
          onClick={toggleSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={combineClasses(
          styles.leftSideContainer,
          !sidebarOpen ? styles.sidebarClosed : styles.sidebarOpen,
        )}
      >
        <div className={styles.brandHeader}>
          <div className={styles.brandHeaderLeft}>
            <div className={styles.logoBadge}>
              <MonogramSVG />
            </div>
            {sidebarOpen && (
              <div className={styles.brandText}>
                <p className={styles.brandName}>THE CHARTERED</p>
                <p className={styles.brandTagline}>Admin Console</p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <button
              type="button"
              className={styles.mobileCloseButton}
              onClick={toggleSidebarOpen(false)}
              aria-label="Close navigation drawer"
            >
              <ArrowIcon
                className={styles.mobileCloseIcon}
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
          )}
        </div>

        <div className={styles.navContainer}>
          {groupedNavigation.map(({ section, items }) => (
            <div key={section} className={styles.navSection}>
              {sidebarOpen && <p className={styles.sectionLabel}>{section}</p>}
              {items.map(renderNavItem)}
            </div>
          ))}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={toggleSidebarOpen(!sidebarOpen)}
          >
            <ArrowIcon
              className={styles.collapseIcon}
              style={{ transform: sidebarOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
            {sidebarOpen && <span>Collapse</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
